import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { type ReactNode, useState } from 'react';

type Kind = 'address' | 'permit' | 'apn';
export interface PermitSearch {
  kind: 'permit' | 'apn';
  q: string;
  state?: string;
}

/**
 * One search box for an address, permit number or APN (#171). The address
 * mode is the autocomplete passed in; the other two search on Enter.
 */
export function SearchBox({
  address,
  onSearch,
  initial,
}: {
  address: ReactNode;
  onSearch: (s: PermitSearch) => void;
  /** The search currently shown (from the URL), so the box reflects it. */
  initial?: Partial<PermitSearch>;
}) {
  const [kind, setKind] = useState<Kind>(initial?.kind ?? 'address');
  const [q, setQ] = useState(initial?.q ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const submit = () => {
    if (kind === 'address' || !q.trim()) return;
    const st = state.trim().toUpperCase();
    onSearch({ kind, q: q.trim(), ...(/^[A-Z]{2}$/.test(st) ? { state: st } : {}) });
  };
  return (
    <Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={kind}
        onChange={(_, v: Kind | null) => v && setKind(v)}
        aria-label="Search by"
        sx={{ mb: 1, backgroundColor: 'background.paper' }}
      >
        <ToggleButton value="address">Address</ToggleButton>
        <ToggleButton value="permit">Permit #</ToggleButton>
        <ToggleButton value="apn">APN</ToggleButton>
      </ToggleButtonGroup>
      {kind === 'address' ? (
        address
      ) : (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
        >
          <TextField
            label={kind === 'permit' ? 'Permit number' : 'APN'}
            placeholder={kind === 'permit' ? '2026-108635 MP' : '0101-0203'}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{
              flex: '1 1 260px',
              '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' },
            }}
            helperText="Press Enter to search"
          />
          <TextField
            label="State (optional)"
            value={state}
            onChange={(e) => setState(e.target.value)}
            inputProps={{ maxLength: 2 }}
            sx={{ width: 150, '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
            helperText="Faster with a state"
          />
          <button type="submit" hidden />
        </Box>
      )}
    </Box>
  );
}
